import React, { createContext, useContext, useState, useEffect, useLayoutEffect } from 'react';

const ThemeContext = createContext();

const VALID_THEMES = ['dark', 'white', 'mid', 'custom'];
const VALID_FONT_SIZES = ['compact', 'normal', 'comfortable'];
const VALID_SIDEBAR_STYLES = ['blur', 'solid', 'glass'];
const VALID_BORDER_RADIUS = ['sharp', 'rounded', 'pill'];

/* ── Preset accent palettes ── */
const ACCENT_PRESETS = [
  { key: 'blue',      color: '#3b82f6', label: 'Blue' },
  { key: 'violet',    color: '#8b5cf6', label: 'Violet' },
  { key: 'rose',      color: '#f43f5e', label: 'Rose' },
  { key: 'emerald',   color: '#10b981', label: 'Emerald' },
  { key: 'amber',     color: '#f59e0b', label: 'Amber' },
  { key: 'cyan',      color: '#06b6d4', label: 'Cyan' },
  { key: 'fuchsia',   color: '#d946ef', label: 'Fuchsia' },
  { key: 'orange',    color: '#f97316', label: 'Orange' },
];

/* ── Color presets (theme + custom color combos) ── */
const COLOR_PRESETS = [
  { key: 'cyberpunk',  label: 'Cyberpunk',  theme: 'custom', color: '#ff006e', accent: '#00f5d4' },
  { key: 'ocean',      label: 'Ocean',      theme: 'custom', color: '#0077b6', accent: '#00b4d8' },
  { key: 'forest',     label: 'Forest',     theme: 'custom', color: '#2d6a4f', accent: '#52b788' },
  { key: 'sunset',     label: 'Sunset',     theme: 'custom', color: '#e85d04', accent: '#faa307' },
  { key: 'lavender',   label: 'Lavender',   theme: 'custom', color: '#7b2cbf', accent: '#c77dff' },
  { key: 'midnight',   label: 'Midnight',   theme: 'dark',   color: '#08080c', accent: '#6366f1' },
  { key: 'arctic',     label: 'Arctic',     theme: 'white',  color: '#ffffff', accent: '#0ea5e9' },
  { key: 'blood',      label: 'Blood',      theme: 'custom', color: '#7f1d1d', accent: '#ef4444' },
];

const ls = (key, fallback) => {
  try { return localStorage.getItem(key) || fallback; } catch { return fallback; }
};

export { ACCENT_PRESETS, COLOR_PRESETS };

export const useTheme = () => {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
};

export const ThemeProvider = ({ children }) => {
  const [theme, setThemeState] = useState(() => {
    const saved = ls('gv-theme', 'dark');
    return VALID_THEMES.includes(saved) ? saved : 'dark';
  });

  const [customColor, setCustomColorState] = useState(() => ls('gv-theme-color', '#1e3a5f'));
  const [accentColor, setAccentColorState] = useState(() => ls('gv-accent-color', '#3b82f6'));
  const [fontSize, setFontSizeState] = useState(() => {
    const saved = ls('gv-font-size', 'normal');
    return VALID_FONT_SIZES.includes(saved) ? saved : 'normal';
  });
  const [sidebarStyle, setSidebarStyleState] = useState(() => {
    const saved = ls('gv-sidebar-style', 'blur');
    return VALID_SIDEBAR_STYLES.includes(saved) ? saved : 'blur';
  });
  const [borderRadius, setBorderRadiusState] = useState(() => {
    const saved = ls('gv-border-radius', 'rounded');
    return VALID_BORDER_RADIUS.includes(saved) ? saved : 'rounded';
  });
  const [reduceMotion, setReduceMotionState] = useState(() => ls('gv-reduce-motion', 'false') === 'true');

  /* ── Sync to DOM ── */
  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
  }, [theme]);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-font-size', fontSize);
  }, [fontSize]);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-sidebar-style', sidebarStyle);
  }, [sidebarStyle]);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-radius', borderRadius);
  }, [borderRadius]);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-reduce-motion', reduceMotion ? 'true' : 'false');
  }, [reduceMotion]);

  useLayoutEffect(() => {
    document.documentElement.style.setProperty('--accent', accentColor);
    /* derive rgb triplet for rgba usage */
    const hex = accentColor.replace('#', '').padEnd(6, '0');
    const r = parseInt(hex.slice(0, 2), 16);
    const g = parseInt(hex.slice(2, 4), 16);
    const b = parseInt(hex.slice(4, 6), 16);
    document.documentElement.style.setProperty('--accent-rgb', `${r},${g},${b}`);
  }, [accentColor]);

  /* ── Persist to localStorage ── */
  useEffect(() => { try { localStorage.setItem('gv-theme', theme); } catch {} }, [theme]);
  useEffect(() => { try { localStorage.setItem('gv-theme-color', customColor); } catch {} }, [customColor]);
  useEffect(() => { try { localStorage.setItem('gv-accent-color', accentColor); } catch {} }, [accentColor]);
  useEffect(() => { try { localStorage.setItem('gv-font-size', fontSize); } catch {} }, [fontSize]);
  useEffect(() => { try { localStorage.setItem('gv-sidebar-style', sidebarStyle); } catch {} }, [sidebarStyle]);
  useEffect(() => { try { localStorage.setItem('gv-border-radius', borderRadius); } catch {} }, [borderRadius]);
  useEffect(() => { try { localStorage.setItem('gv-reduce-motion', String(reduceMotion)); } catch {} }, [reduceMotion]);

  const setTheme = (t) => setThemeState(VALID_THEMES.includes(t) ? t : 'dark');
  const setCustomColor = (color) => setCustomColorState(color);
  const setAccentColor = (color) => setAccentColorState(color);
  const setFontSize = (s) => setFontSizeState(VALID_FONT_SIZES.includes(s) ? s : 'normal');
  const setSidebarStyle = (s) => setSidebarStyleState(VALID_SIDEBAR_STYLES.includes(s) ? s : 'blur');
  const setBorderRadius = (r) => setBorderRadiusState(VALID_BORDER_RADIUS.includes(r) ? r : 'rounded');
  const setReduceMotion = (v) => setReduceMotionState(!!v);
  const toggleTheme = () => setThemeState(prev => prev === 'dark' ? 'white' : 'dark');

  /* Apply a full preset */
  const applyPreset = (preset) => {
    setTheme(preset.theme);
    if (preset.color) setCustomColor(preset.color);
    if (preset.accent) setAccentColor(preset.accent);
  };

  return (
    <ThemeContext.Provider value={{
      theme, setTheme, toggleTheme,
      customColor, setCustomColor,
      accentColor, setAccentColor,
      fontSize, setFontSize,
      sidebarStyle, setSidebarStyle,
      borderRadius, setBorderRadius,
      reduceMotion, setReduceMotion,
      applyPreset,
    }}>
      {children}
    </ThemeContext.Provider>
  );
};
